/**
 * #53 / DEC-360 — the Map View's optional layers: Pickup sites, Disposal sites, Route lines.
 *
 * Partially reverses DEC-069, which removed the route line and the pickup/disposal dots
 * because they made the map busy and were unexplained. So: every layer is OFF by default
 * (the map looks exactly as before until someone opens Layers), each one has a legend
 * entry while it is on, and two trucks bound for one site draw ONE marker with a count.
 *
 * What is drawn for a driver comes from the shared rule (`@lodex-pro/shared/map-route`):
 * the line shows what is LEFT of the load, nothing is drawn for a paused or finished job,
 * and a missing point is never invented.
 *
 * Rendering is WebGL (GeoJSON sources + circle/symbol/line layers), not DOM markers:
 * toggling is a visibility flip, and it stays fast with many drivers. Driver pins remain
 * DOM markers, so they always sit above these layers.
 *
 * The map is SHARED with the Geofence editor (MapProvider): `removeOpsLayers` must run when
 * this surface releases it, and `setStyle` wipes added layers, so `ensureOpsLayers` runs on
 * every `style.load`.
 */
import type { AnyLayer } from 'mapbox-gl';
import type { DriverPosition } from '@lodex-pro/shared';
import { MAP_COLOURS } from '../../lib/map-colours';
import { hasPoint, routePlan, type RoutePoint } from '@lodex-pro/shared/map-route';

export interface MapLayerPrefs {
  readonly pickups: boolean;
  readonly disposals: boolean;
  readonly routes: boolean;
}

/** All off: the map looks exactly as it did before #53 until someone turns a layer on. */
export const DEFAULT_LAYER_PREFS: MapLayerPrefs = {
  pickups: false,
  disposals: false,
  routes: false,
};

export const LAYER_COLORS = {
  pickup: MAP_COLOURS.pickup, // teal: amber already means a Busy driver pin
  disposal: MAP_COLOURS.disposal,
  route: MAP_COLOURS.route,
} as const;

/** Same 2 h cutoff that dims a driver's pin to 40% (DriverMap `veryStale`). */
export const STALE_MINUTES = 120;
const STALE_OPACITY = 0.4;

const SRC_PICKUPS = 'lx-ops-pickups';
const SRC_DISPOSALS = 'lx-ops-disposals';
const SRC_ROUTES = 'lx-ops-routes';

export const LAYER_IDS = {
  routes: ['lx-ops-routes-line'],
  pickups: ['lx-ops-pickups-circle', 'lx-ops-pickups-count'],
  disposals: ['lx-ops-disposals-circle', 'lx-ops-disposals-count'],
} as const;

/** The clickable layers, for the site popup. */
export const SITE_CIRCLE_LAYERS = [LAYER_IDS.pickups[0], LAYER_IDS.disposals[0]] as const;

export interface SiteProps {
  readonly key: string;
  readonly kind: 'PICKUP' | 'DISPOSAL';
  readonly name: string;
  /** Trucks bound for this site. */
  readonly count: number;
  /** Their names, for the popup. */
  readonly drivers: string;
  /** True only when EVERY truck bound here is stale — one live truck keeps it bright. */
  readonly stale: boolean;
}

export interface LayerData {
  readonly pickups: GeoJSON.FeatureCollection<GeoJSON.Point, SiteProps>;
  readonly disposals: GeoJSON.FeatureCollection<GeoJSON.Point, SiteProps>;
  readonly routes: GeoJSON.FeatureCollection<
    GeoJSON.LineString,
    { readonly driverId: string; readonly stale: boolean }
  >;
}

function isStaleAt(positionAt: string, nowMs: number): boolean {
  const t = Date.parse(positionAt);
  return Number.isNaN(t) ? true : nowMs - t > STALE_MINUTES * 60_000;
}

interface SiteAcc {
  key: string;
  kind: 'PICKUP' | 'DISPOSAL';
  name: string;
  lng: number;
  lat: number;
  drivers: string[];
  allStale: boolean;
}

function addSite(
  acc: Map<string, SiteAcc>,
  site: Omit<SiteAcc, 'drivers' | 'allStale'>,
  driverName: string,
  stale: boolean,
): void {
  const existing = acc.get(site.key);
  if (existing === undefined) {
    acc.set(site.key, { ...site, drivers: [driverName], allStale: stale });
    return;
  }
  existing.drivers.push(driverName);
  existing.allStale = existing.allStale && stale;
}

function toSiteFeatures(
  acc: Map<string, SiteAcc>,
): GeoJSON.FeatureCollection<GeoJSON.Point, SiteProps> {
  return {
    type: 'FeatureCollection',
    features: [...acc.values()].map((s) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
      properties: {
        key: s.key,
        kind: s.kind,
        name: s.name,
        count: s.drivers.length,
        drivers: [...s.drivers].sort((a, b) => a.localeCompare(b)).join(', '),
        stale: s.allStale,
      },
    })),
  };
}

/**
 * Build the three layers' data from the positions feed. Pure: `nowMs` is passed in so
 * staleness is testable. Sites are merged by `pickupKey` / `disposalKey` (#53 PR 1); a
 * row without a key (older API) falls back to a per-driver key, which only means no merge.
 */
export function buildLayerData(positions: ReadonlyArray<DriverPosition>, nowMs: number): LayerData {
  const pickups = new Map<string, SiteAcc>();
  const disposals = new Map<string, SiteAcc>();
  const routes: Array<LayerData['routes']['features'][number]> = [];

  for (const p of positions) {
    const hasPickup = hasPoint(p.pickupLat, p.pickupLng);
    const hasDisposal = hasPoint(p.disposalLat, p.disposalLng);
    const plan = routePlan(p.jobStatusCode, hasPickup, hasDisposal);
    const stale = isStaleAt(p.positionAt, nowMs);

    if (plan.showPickup && p.pickupLat !== null && p.pickupLng !== null) {
      addSite(
        pickups,
        {
          key: p.pickupKey ?? `P:${p.driverId}`,
          kind: 'PICKUP',
          name: p.siteName ?? 'Pickup',
          lng: p.pickupLng,
          lat: p.pickupLat,
        },
        p.driverName,
        stale,
      );
    }
    if (plan.showDisposal && p.disposalLat !== null && p.disposalLng !== null) {
      addSite(
        disposals,
        {
          key: p.disposalKey ?? `D:${p.driverId}`,
          kind: 'DISPOSAL',
          name: p.disposalName ?? 'Disposal',
          lng: p.disposalLng,
          lat: p.disposalLat,
        },
        p.driverName,
        stale,
      );
    }

    const point = (which: RoutePoint): [number, number] | null => {
      if (which === 'DRIVER') return [p.lng, p.lat];
      if (which === 'PICKUP') {
        return p.pickupLng === null || p.pickupLat === null ? null : [p.pickupLng, p.pickupLat];
      }
      return p.disposalLng === null || p.disposalLat === null
        ? null
        : [p.disposalLng, p.disposalLat];
    };
    for (const [from, to] of plan.segments) {
      const a = point(from);
      const b = point(to);
      if (a === null || b === null) continue; // routePlan only names known points
      routes.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [a, b] },
        properties: { driverId: p.driverId, stale },
      });
    }
  }

  return {
    pickups: toSiteFeatures(pickups),
    disposals: toSiteFeatures(disposals),
    routes: { type: 'FeatureCollection', features: routes },
  };
}

/**
 * The slice of the Mapbox map these helpers use, so a test can pass a fake. mapbox-gl's
 * `Map` satisfies it structurally.
 */
export interface LayerHost {
  getSource(id: string): unknown;
  getLayer(id: string): unknown;
  addSource(id: string, source: { type: 'geojson'; data: GeoJSON.FeatureCollection }): unknown;
  addLayer(layer: AnyLayer): unknown;
  removeLayer(id: string): unknown;
  removeSource(id: string): unknown;
  setLayoutProperty(layerId: string, name: string, value: unknown): unknown;
}

interface SettableSource {
  setData(data: GeoJSON.FeatureCollection): void;
}

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

function visibilityOf(on: boolean): 'visible' | 'none' {
  return on ? 'visible' : 'none';
}

const STALE_EXPR = ['case', ['get', 'stale'], STALE_OPACITY, 1];

function siteLayers(
  src: string,
  ids: readonly [string, string],
  color: string,
  on: boolean,
): Array<Record<string, unknown>> {
  return [
    {
      id: ids[0],
      type: 'circle',
      source: src,
      layout: { visibility: visibilityOf(on) },
      paint: {
        'circle-radius': ['case', ['>', ['get', 'count'], 1], 10, 7],
        'circle-color': color,
        'circle-stroke-color': MAP_COLOURS.ink,
        'circle-stroke-width': 2,
        'circle-opacity': STALE_EXPR,
        'circle-stroke-opacity': STALE_EXPR,
      },
    },
    {
      // The truck count, only where more than one truck shares the site.
      id: ids[1],
      type: 'symbol',
      source: src,
      filter: ['>', ['get', 'count'], 1],
      layout: {
        visibility: visibilityOf(on),
        'text-field': ['to-string', ['get', 'count']],
        'text-size': 11,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': MAP_COLOURS.ink, 'text-opacity': STALE_EXPR },
    },
  ];
}

/**
 * Add the sources and layers if they are missing (idempotent), with visibility from
 * `prefs`. Order matters: routes first so the site markers draw over the lines.
 */
export function ensureOpsLayers(map: LayerHost, prefs: MapLayerPrefs): void {
  if (map.getSource(SRC_ROUTES) === undefined) {
    map.addSource(SRC_ROUTES, { type: 'geojson', data: EMPTY });
  }
  if (map.getSource(SRC_PICKUPS) === undefined) {
    map.addSource(SRC_PICKUPS, { type: 'geojson', data: EMPTY });
  }
  if (map.getSource(SRC_DISPOSALS) === undefined) {
    map.addSource(SRC_DISPOSALS, { type: 'geojson', data: EMPTY });
  }
  const layers: Array<Record<string, unknown>> = [
    {
      id: LAYER_IDS.routes[0],
      type: 'line',
      source: SRC_ROUTES,
      layout: { visibility: visibilityOf(prefs.routes), 'line-cap': 'round' },
      paint: {
        'line-color': LAYER_COLORS.route,
        'line-width': 2,
        // Straight and dashed: "where they are headed", not the road (design C5).
        'line-dasharray': [2, 2],
        'line-opacity': ['case', ['get', 'stale'], STALE_OPACITY, 0.9],
      },
    },
    ...siteLayers(SRC_DISPOSALS, LAYER_IDS.disposals, LAYER_COLORS.disposal, prefs.disposals),
    ...siteLayers(SRC_PICKUPS, LAYER_IDS.pickups, LAYER_COLORS.pickup, prefs.pickups),
  ];
  for (const layer of layers) {
    // The definitions are plain objects (Mapbox expression arrays do not infer as its
    // strict tuple types); their content is pinned by map-layers.test.ts.
    if (map.getLayer(layer['id'] as string) === undefined)
      map.addLayer(layer as unknown as AnyLayer);
  }
}

/** Push fresh data into the three sources (no-op for any source not added yet). */
export function setOpsLayerData(map: LayerHost, data: LayerData): void {
  const set = (id: string, fc: GeoJSON.FeatureCollection): void => {
    const src = map.getSource(id) as SettableSource | undefined;
    src?.setData(fc);
  };
  set(SRC_PICKUPS, data.pickups);
  set(SRC_DISPOSALS, data.disposals);
  set(SRC_ROUTES, data.routes);
}

/** Show or hide each layer to match `prefs`. */
export function applyLayerVisibility(map: LayerHost, prefs: MapLayerPrefs): void {
  for (const which of ['pickups', 'disposals', 'routes'] as const) {
    for (const id of LAYER_IDS[which]) {
      if (map.getLayer(id) !== undefined) {
        map.setLayoutProperty(id, 'visibility', visibilityOf(prefs[which]));
      }
    }
  }
}

/** Remove every layer and source this surface added — the shared map moves on to others. */
export function removeOpsLayers(map: LayerHost): void {
  for (const id of [...LAYER_IDS.pickups, ...LAYER_IDS.disposals, ...LAYER_IDS.routes]) {
    if (map.getLayer(id) !== undefined) map.removeLayer(id);
  }
  for (const id of [SRC_PICKUPS, SRC_DISPOSALS, SRC_ROUTES]) {
    if (map.getSource(id) !== undefined) map.removeSource(id);
  }
}

export function anyLayerOn(prefs: MapLayerPrefs): boolean {
  return prefs.pickups || prefs.disposals || prefs.routes;
}
